import React, { useState } from "react";
import {
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiDollarSign,
} from "react-icons/fi";
import { useGetAdminFundsQuery } from "../../redux/slices/api/adminApiSlice";
import { Bars } from "react-loader-spinner";
import WalletDisplay from "../../components/common/WalletDisplay";

const AdminFunds = () => {
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useGetAdminFundsQuery({
    page,
    limit: 20,
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount, token) => {
    if (!amount) return "—";
    return `${parseFloat(amount).toFixed(4)} ${token || "ETH"}`;
  };

  const getFunderName = (fund) => {
    const funder = fund.funder_id;
    if (!funder) return "N/A";
    if (funder.fundername) return funder.fundername;
    return `${funder.firstname || ""} ${funder.lastname || ""}`.trim() || "N/A";
  };

  const getExplorerUrl = (txHash, blockchain) => {
    const explorers = {
      ethereum: "https://etherscan.io",
      sepolia: "https://sepolia.etherscan.io",
      matic: "https://polygonscan.com",
      bnb: "https://bscscan.com",
      arbitrum: "https://arbiscan.io",
      optimism: "https://optimistic.etherscan.io",
      zkSync: "https://explorer.zksync.io",
      skale: "https://explorer.skale.network",
      calypso: "https://honorable-steel-rasalhague.explorer.mainnet.skalenodes.com",
      celo: "https://celoscan.io",
    };
    const base = explorers[blockchain] || "https://etherscan.io";
    return `${base}/tx/${txHash}`;
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">Failed to load funds. Please try again.</p>
        <button
          onClick={refetch}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Funds</h1>
          <p className="text-gray-500">
            View all funding transactions ({data?.pagination?.total || 0} total)
          </p>
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-light-primary transition-colors"
        >
          <FiRefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Funds Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Bars height="40" width="40" color="#8538FF" ariaLabel="loading" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Project
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Funder
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Amount
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Transaction
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.funds?.map((fund) => (
                    <tr key={fund._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                            <FiDollarSign className="text-green-600" size={18} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-secondary truncate max-w-[200px]">
                              {fund.project_id?.title || "Unknown Project"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {fund.funder_id?.images?.profile ? (
                              <img
                                src={fund.funder_id.images.profile}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-primary text-xs font-semibold">
                                {getFunderName(fund)[0]?.toUpperCase() || "?"}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <span className="text-gray-600 truncate max-w-[120px] block">
                              {getFunderName(fund)}
                            </span>
                            {fund.funder_address && (
                              <span className="text-xs text-gray-400 truncate block">
                                <WalletDisplay address={fund.funder_address} />
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium text-green-600">
                          {formatAmount(fund.amount, fund.token)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {fund.tx_hash ? (
                          <a
                            href={getExplorerUrl(fund.tx_hash, fund.blockchain)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline font-mono text-sm"
                          >
                            {fund.tx_hash.slice(0, 8)}...{fund.tx_hash.slice(-6)}
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600 text-sm">
                          {formatDate(fund.createdAt)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {data?.funds?.length === 0 && (
              <div className="text-center py-12">
                <FiDollarSign className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500">No funds found</p>
              </div>
            )}

            {/* Pagination */}
            {data?.pagination && data.pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing{" "}
                  {(data.pagination.page - 1) * data.pagination.limit + 1} to{" "}
                  {Math.min(
                    data.pagination.page * data.pagination.limit,
                    data.pagination.total
                  )}{" "}
                  of {data.pagination.total} funds
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {data.pagination.pages}
                  </span>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(data.pagination.pages, p + 1))
                    }
                    disabled={page === data.pagination.pages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminFunds;
